import React, { useState } from 'react';
import type { Member } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useMemberDrawerStore } from '@hero/hooks-shared';
import { useMemberApi } from '@hero/api-hooks';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import { MemberSummaryTab } from './MemberSummaryTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';

export interface MemberDrawerProps {
  syncUrl?: boolean | string;
}

interface MemberDrawerContentProps {
  member: Member;
  onClose: () => void;
}

const MemberDrawerContent: React.FC<MemberDrawerContentProps> = ({
  member,
  onClose,
}) => {
  const { t } = useTranslation(['m-member']);
  const [activeTab, setActiveTab] = useState('summary');

  const tabs = [{ id: 'summary', label: t('drawer.tabs.summary') }];

  return (
    <>
      <DrawerHeader title={member.name || ''} subtitle={member.phone} onClose={onClose} />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>
        <MemberSummaryTab member={member} />
      </DrawerContent>
    </>
  );
};

/**
 * Self-contained Member Drawer.
 *
 * Render ONE instance per page. Open from anywhere via:
 * ```ts
 * useMemberDrawerStore.getState().open(memberId)
 * ```
 */
export const MemberDrawer: React.FC<MemberDrawerProps> = ({ syncUrl }) => (
  <StackedDrawer<Member>
    useStore={useMemberDrawerStore}
    useItemApi={id => useMemberApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="memberId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="User Details"
    description="View member information"
    renderContent={(member, onClose) => (
      <MemberDrawerContent member={member} onClose={onClose} />
    )}
  />
);
